import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useGetFollowersInfiniteQuery, useGetFollowingInfiniteQuery } from '../../apis';
import { Avatar } from '../../components/Avatar/Avatar';
import { Spinner, SpinnerVariants, UserName } from '../../UI';

interface FollowListProps {
  type: 'followers' | 'following';
}

// Renders in the center column (router Outlet) like a profile sub-page.
export const FollowList: React.FC<FollowListProps> = ({ type }) => {
  const { userId } = useParams<{ userId: string }>();

  // Only one query runs — the other is skipped — but hooks must be called
  // unconditionally, so we gate with `skip`. Cursor-paginated: `fetchNextPage`
  // appends the next page so large profiles don't load every follower at once.
  const followers = useGetFollowersInfiniteQuery(userId ?? '', { skip: !userId || type !== 'followers' });
  const following = useGetFollowingInfiniteQuery(userId ?? '', { skip: !userId || type !== 'following' });
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    type === 'followers' ? followers : following;

  const heading = type === 'followers' ? 'Followers' : 'Following';
  const users = data?.pages.flat() ?? [];

  return (
    <div className="flex flex-1 flex-col overflow-y-scroll no-scrollbar">
      <h2 className="text-app-lg2 font-semibold text-center my-app-base">{heading}</h2>

      {isLoading ? (
        <div className="flex justify-center py-app-lg">
          <Spinner variant={SpinnerVariants.SMALL_CIRCLE} />
        </div>
      ) : error ? (
        <p className="text-center text-app-gray py-app-lg">Unable to load {heading.toLowerCase()}.</p>
      ) : users.length === 0 ? (
        <p className="text-center text-app-gray py-app-lg">
          {type === 'followers' ? 'No followers yet.' : 'Not following anyone yet.'}
        </p>
      ) : (
        <>
          <ul>
            {users.map((user) => (
              <li key={`follow-${user._id}`} className="flex items-center p-app-base border-b border-app-card">
                <Avatar
                  userId={user._id}
                  displayName={user.displayName ?? undefined}
                  userName={user.userName ?? undefined}
                  imageUrl={user.profileImgUrl ?? undefined}
                  linkLabel={user.displayName ?? user.userName ?? 'View profile'}
                />
                <NavLink to={`/profile/${user._id}`} className="flex flex-col px-app-base hover:opacity-80">
                  <span className="font-bold">{user.displayName}</span>
                  <UserName userName={user.userName ?? undefined} />
                </NavLink>
              </li>
            ))}
          </ul>

          {hasNextPage && (
            <div className="flex justify-center py-app-base">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="text-app-primary font-semibold disabled:opacity-50"
              >
                {isFetchingNextPage ? <Spinner variant={SpinnerVariants.SMALL_CIRCLE} /> : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
